import { useState } from "react";
import { Trash2, X, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * Botão de exclusão com senha inline.
 * - Usa DELETE {endpoint} com body { password }.
 * - Mostra modal com input de senha antes de disparar.
 * - `label` opcional para prefixar toast (ex: "Lead", "Orçamento").
 */
export default function PasswordDeleteButton({ endpoint, label = "Registro", onDeleted, size = "sm", testId }) {
    const [open, setOpen] = useState(false);
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!password) return;
        setBusy(true);
        try {
            await api.delete(endpoint, { data: { password } });
            toast.success(`${label} excluído`);
            setOpen(false);
            setPassword("");
            onDeleted?.();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Falha ao excluir");
        } finally { setBusy(false); }
    };

    const iconSize = size === "sm" ? 12 : 14;

    return (
        <>
            <button
                type="button"
                data-testid={testId}
                onClick={(e) => { e.stopPropagation(); setOpen(true); }}
                className={`${size === "sm" ? "h-8 px-2" : "h-10 px-3"} border border-neutral-300 hover:bg-red-50 hover:border-red-300 text-red-700 text-xs font-medium flex items-center gap-1`}
                title="Excluir"
            >
                <Trash2 size={iconSize} /> {size === "sm" ? null : "Excluir"}
            </button>

            {open && (
                <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="delete-modal" onClick={() => !busy && setOpen(false)}>
                    <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white max-w-sm w-full border-2 border-red-300">
                        <div className="p-5 border-b border-neutral-200 flex items-start justify-between gap-3">
                            <div>
                                <div className="text-xs font-mono uppercase tracking-widest text-red-700">// exclusão permanente</div>
                                <h3 className="mt-1 font-heading font-black text-xl tracking-tight">Excluir {label.toLowerCase()}</h3>
                                <p className="mt-2 text-xs text-neutral-600">Digite a senha de exclusão para confirmar. Ação irreversível.</p>
                            </div>
                            <button type="button" onClick={() => !busy && setOpen(false)} className="text-neutral-400 hover:text-neutral-900"><X size={16} /></button>
                        </div>
                        <div className="p-5">
                            <label className="block">
                                <span className="text-xs font-mono uppercase tracking-widest text-neutral-500">Senha</span>
                                <input
                                    type="password"
                                    autoFocus
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    data-testid="delete-password-input"
                                    className="mt-1 w-full h-11 px-3 border border-neutral-300 focus:outline-none focus:border-red-500"
                                />
                            </label>
                        </div>
                        <div className="p-5 border-t border-neutral-200 flex items-center justify-end gap-2">
                            <button type="button" onClick={() => setOpen(false)} disabled={busy} className="h-10 px-4 border border-neutral-300 text-sm hover:bg-neutral-50">Cancelar</button>
                            <button type="submit" disabled={busy || !password} data-testid="delete-confirm" className="h-10 px-4 bg-red-600 text-white text-sm font-heading font-bold uppercase tracking-wider hover:bg-red-700 disabled:opacity-50 flex items-center gap-2">
                                {busy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Excluir
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </>
    );
}
